import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatError } from "../../lib/api";

export function Composer({ onPublished }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [tier, setTier] = useState("fan");
  const [ppv, setPpv] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCaption("");
    setPpv("");
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const publish = async () => {
    if (!file && !caption.trim()) return toast.error("Add a photo, video or caption");
    setBusy(true);
    try {
      let media_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/media", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
        media_id = data.media_id;
      }
      await api.post("/admin/posts", { caption, tier, media_id, ppv_price: ppv ? Number(ppv) : null });
      toast.success("Post published");
      reset();
      onPublished?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const isVideo = file?.type?.startsWith("video/");

  return (
    <div data-testid="admin-composer" className="grid lg:grid-cols-2 gap-6">
      <div className="border border-dashed border-[#0E1B3D]/20 bg-[#FFFFFF] min-h-[340px] relative grid place-items-center overflow-hidden">
        {preview ? (
          <>
            {isVideo ? <video src={preview} controls className="w-full h-full max-h-[520px] object-contain" /> : <img src={preview} alt="Preview" className="w-full h-full max-h-[520px] object-contain" />}
            <button data-testid="admin-remove-media-btn" onClick={reset} className="absolute top-3 right-3 bg-[#FFF8EE]/80 p-2 text-[#0E1B3D] hover:text-[#D7263D]" aria-label="Remove"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <button data-testid="admin-new-post-btn" onClick={() => inputRef.current?.click()} className="flex flex-col items-center gap-4 text-[#26355A] hover:text-[#D7263D] transition-colors p-10">
            <ImagePlus className="w-10 h-10" />
            <span className="text-sm">Upload a photo or video</span>
            <span className="text-xs text-[#4A5878]">JPG, PNG, WEBP, MP4, MOV · up to 200MB</span>
          </button>
        )}
        <input ref={inputRef} data-testid="admin-file-input" type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <div className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] p-6 sm:p-8 flex flex-col gap-6">
        <label className="block">
          <span className="font-mono-x text-[0.65rem] tracking-[0.22em] uppercase text-[#4A5878]">Caption</span>
          <textarea data-testid="admin-post-caption-input" rows={6} value={caption} maxLength={2000} onChange={(e) => setCaption(e.target.value)} placeholder="Say something to your members…"
            className="mt-3 w-full bg-[#FFF8EE] border border-[#0E1B3D]/15 focus:border-[#D7263D] outline-none p-4 text-sm text-[#0E1B3D] placeholder:text-[#6B7894] resize-none" />
        </label>
        <div>
          <span className="font-mono-x text-[0.65rem] tracking-[0.22em] uppercase text-[#4A5878]">Who can see this</span>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger data-testid="admin-tier-select" className="mt-3 bg-[#FFF8EE] border-[#0E1B3D]/15 h-12 text-[#0E1B3D]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#0E1B3D]/15 text-[#0E1B3D]">
              <SelectItem value="fan" data-testid="admin-tier-option-fan">Fan and above</SelectItem>
              <SelectItem value="vip" data-testid="admin-tier-option-vip">VIP and above</SelectItem>
              <SelectItem value="inner" data-testid="admin-tier-option-inner">Inner Circle only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="block">
          <span className="font-mono-x text-[0.65rem] tracking-[0.22em] uppercase text-[#4A5878]">Pay-per-view price (optional, USD)</span>
          <input data-testid="admin-ppv-price-input" type="number" min="3" max="1000" step="1" value={ppv} onChange={(e) => setPpv(e.target.value)} placeholder="Leave empty for a regular post"
            className="mt-3 w-full bg-[#FFF8EE] border border-[#0E1B3D]/15 focus:border-[#D7263D] outline-none px-4 h-12 text-sm text-[#0E1B3D] placeholder:text-[#6B7894]" />
          <span className="mt-2 block text-xs text-[#4A5878]">Members of the chosen tier pay this once to unlock the post. Minimum $3.</span>
        </label>
        {busy && file && <div className="h-1 bg-[#0E1B3D]/10"><div className="h-full bg-[#D7263D] transition-[width]" style={{ width: `${progress}%` }} /></div>}
        <button data-testid="admin-publish-btn" disabled={busy} onClick={publish} className="gold-btn mt-auto py-4 text-sm font-semibold tracking-[0.14em] uppercase flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish to feed
        </button>
      </div>
    </div>
  );
}
